import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchFunctionDefinitions } from "../actions/functions";
import {
  NewFunctionDestination,
  SaaSDestination,
} from "../destinations/destinations";
import { FunctionSlug } from "../destinations/types";
import { FunctionDefinition } from "../functions/types";

export const useFunctionDestinations = (): Record<string, SaaSDestination> => {
  const {
    functionDefs,
    isLoaded,
  }: { functionDefs: FunctionDefinition[]; isLoaded: boolean } = useSelector(
    (state) => (state as any).functionDefs,
  );

  const dispatch = useDispatch();

  // Fetch function definitions in useEffect to avoid dispatching during render
  useEffect(() => {
    if (!isLoaded) {
      dispatch(fetchFunctionDefinitions());
    }
  }, [isLoaded, dispatch]);

  // Memoized because callers use the result as a `useMemo`/`select` dependency;
  // rebuilding the lookup every render would defeat every one of those.
  return useMemo(
    () => (isLoaded ? buildFunctionDestinationsLookup(functionDefs) : {}),
    [isLoaded, functionDefs],
  );
};

export function buildFunctionDestinationsLookup(
  functionDefinitions: FunctionDefinition[],
) {
  const functionDestinations = functionDefinitions
    .filter((functionDef) => functionDef.type === "DESTINATION")
    .map((functionDef) => {
      return NewFunctionDestination({
        slug: functionDef.id,
        name: functionDef.name,
        config: functionDef.config || {
          configFields: [],
        },
      });
    });

  const functionDestinationsMap: Record<FunctionSlug, SaaSDestination> = {};

  for (const functionDestination of functionDestinations) {
    functionDestinationsMap[functionDestination.slug] = functionDestination;
  }

  return functionDestinationsMap;
}
